import Swal from 'sweetalert2';
import { buttonClasses } from '@components/ui/button/CustomButton';
import ErrorIcon from '../../assets/Error.svg';
import SuccessIcon from '../../assets/Success.svg';
import InfoIcon from '../../assets/Info.svg';
import InfoRedIcon from '../../assets/InfoRed.svg';
import { isPanelVerificationPromptActive } from '../../utils/panelVerification';

// HeroUI/React Aria modals mark everything outside the active modal as
// inert (via ariaHideOutside's MutationObserver) so it can't receive
// pointer/keyboard events. SweetAlert2 mounts its container directly on
// document.body, outside React's tree, so with a HeroUI modal open behind
// it, the observer swallows it too — the alert renders but its buttons
// don't respond to clicks until the underlying modal unmounts. Marking
// the container with data-react-aria-top-layer opts it out of that.
const markAsTopLayer = () => {
  const container = Swal.getContainer();
  if (container) {
    container.setAttribute('data-react-aria-top-layer', 'true');
  }
};

// ─── Estilos del design system ────────────────────────────────────────────────

// Variante `Modal Alerta` de la página `09. Modales` del Figma (`115:1041`).
// El estilado sale de la API de SweetAlert2, no de pelear su CSS: `buttonsStyling`
// en false quita la clase `swal2-styled`, cuyo `border: 0` también anulaba el
// `border-style`, y `reverseButtons` deja cancelar a la izquierda.

/** 56×56, el tamaño del icono de estado. */
const ALERT_ICON_SIZE = 56;

// El diseño dibuja el popup a 366×274 con padding 16/15, pero **son las medidas
// de la variante con subtítulo**: son un ejemplo, no un contrato. Con un solo
// mensaje —el caso de 49 de los 52 llamadores— el alto fijo dejaba un hueco
// vacío enorme, y 366 con 15 de padding daba una caja estrecha para dos botones
// de 150 más sus 16 de separación (316 de los 336 útiles).
//
// Así que el alto es **libre** (crece con el contenido) y las otras dos medidas
// se alinean con `CustomModal`, que ya es el marco de los otros 33 modales:
// ancho 420 y padding 24. Criterio propio, no especificación.
const ALERT_LAYOUT = {
  width: 420,
  padding: '24px',
  buttonsStyling: false,
  reverseButtons: true,
  // La equis va en las cuatro alertas: el diseño la dibuja en la variante
  // `Modal Alerta` (`113:1036`) sin condicionarla. Solo la pedían `logout` y
  // `confirm`; `success` y `error` se cerraban únicamente por el botón.
  showCloseButton: true,
};

// ⚠️ El subtítulo NO se puede estilar desde `customClass.htmlContainer`.
//
// SweetAlert2 inyecta su hoja en tiempo de ejecución, así que gana la cascada
// ante cualquier clase de igual especificidad. Y su regla del subtítulo,
// `div:where(.swal2-container) .swal2-html-container`, es (0,1,0) —`:where()` no
// aporta especificidad— igual que `.text-body-2`. Resultado medido: sus
// `font-size:1.125em` (18px), `font-weight:normal`, `line-height:normal`,
// `color:inherit` y `margin:1em 1.6em .3em` pisaban TODO lo que se le pasaba a
// ese slot. De ahí el bug: **el subtítulo salía a 18px, más grande que el
// título de 16**, y sin su color ni sus márgenes.
//
// El título no tiene el problema: su regla es `h2:where(.swal2-title)`, que al
// llevar el nombre de etiqueta queda en (0,0,1) y pierde ante una clase.
//
// La solución es la misma que en `CustomInput`: apuntar al hijo desde el padre
// con una variante arbitraria. Emite `.\[\&_\.swal2-html-container\]\:… \
// .swal2-html-container`, que es (0,2,0) y gana sin `!important`.
const ALERT_SUBTITLE_CLASSES = [
  '[&_.swal2-html-container]:m-0',
  '[&_.swal2-html-container]:p-0',
  // `body-2` es 14/16 y el diseño pide 14/20, así que el alto de línea se
  // sobrescribe — mismo hueco de la escala que ya tiene `CustomAlert`.
  '[&_.swal2-html-container]:text-body-2',
  '[&_.swal2-html-container]:leading-5',
  '[&_.swal2-html-container]:text-fg-tertiary',
].join(' ');

const ALERT_CLASSES = {
  // Sin alto mínimo: el popup mide lo que mide su contenido, así que la variante
  // de un solo mensaje no arrastra el hueco de la que lleva subtítulo. `gap-5`
  // son los 20 entre bloques que ya usa el cuerpo de `CustomModal`.
  popup: `rounded-lg shadow-standard gap-5 ${ALERT_SUBTITLE_CLASSES}`,
  // El grid del popup estira sus hijos; el icono se centra por margen.
  image: 'my-0 mx-auto',
  title: 'p-0 text-subtitle-1 text-fg-secondary',
  actions: 'w-full m-0 p-0 flex justify-end gap-4',
  // Sin `mb-0` su margen negativo de -1.2em colapsa la fila y la equis se solapa
  // con el icono. `text-2xl` da el aspa de 12 sobre un área táctil de 24.
  closeButton:
    'size-6 mb-0 justify-self-end text-2xl leading-none text-icon hover:text-icon focus:shadow-none',
};

// El diseño usa los botones del design system, así que las clases salen de
// `CustomButton` y no se replican acá.
const PRIMARY_BUTTON = buttonClasses('primary');
const SECONDARY_BUTTON = buttonClasses('secondary');

// En el diseño (`115:1041`) la **información principal es el Título**, y el
// subtítulo es un complemento opcional —la variante `showSubtitle` del propio
// componente lo confirma—. Pero 49 de los 52 llamadores pasan `('', mensaje)`,
// así que el mensaje caía en el subtítulo: la alerta salía **sin título** y con
// su único texto en el rol más chico y más claro de los dos.
//
// Estas dos funciones normalizan eso en un solo sitio: con un solo texto, ese
// texto es el título. Solo cuando llegan los dos, el segundo es el subtítulo.
// Así no hay que tocar los 52 llamadores ni el orden de sus argumentos.
//
// La diferencia entre las dos es si el texto admite HTML: se conserva la
// semántica que ya tenía cada alerta. `title` de SweetAlert2 interpreta HTML;
// `titleText` lo escapa.
const withHtmlBody = (title, text) =>
  title ? { title, html: text } : { title: text };

const withPlainBody = (title, text) =>
  title ? { title, text } : { titleText: text };

/** Config común de las cuatro alertas. */
const alertOptions = (extra = {}) => ({
  ...ALERT_LAYOUT,
  imageWidth: ALERT_ICON_SIZE,
  imageHeight: ALERT_ICON_SIZE,
  didOpen: markAsTopLayer,
  ...extra,
  customClass: {
    ...ALERT_CLASSES,
    confirmButton: PRIMARY_BUTTON,
    cancelButton: SECONDARY_BUTTON,
    ...extra.customClass,
  },
});

// ─── Alertas ──────────────────────────────────────────────────────────────────

export const successAlert = (
  title,
  text,
  action = () => {},
  allowOutsideClick = true,
  confirmButtonText,
) => {
  Swal.fire(
    alertOptions({
      ...withHtmlBody(title, text),
      imageUrl: SuccessIcon,
      confirmButtonText: confirmButtonText || 'Entendido',
      allowOutsideClick,
    }),
  ).then((result) => {
    if (result.isConfirmed || result.isDismissed) {
      action();
    }
  });
};

export const errorAlert = (title, text, action = () => {}) => {
  // Un 1185 ya abrió el modal o el aviso de verificación del panel.
  if (isPanelVerificationPromptActive()) return;
  Swal.fire(
    alertOptions({
      ...withPlainBody(title, text),
      imageUrl: ErrorIcon,
      confirmButtonText: 'Entendido',
    }),
  ).then((result) => {
    if (result.isConfirmed) {
      action();
    }
  });
};

// modal para validar el logout
// validate logout modal
export const sweetalertLogout = (title, text, logout) => {
  Swal.fire(
    alertOptions({
      ...withPlainBody(title, text),
      // El `icon: 'warning'` propio de SweetAlert2 no está en la paleta.
      imageUrl: InfoIcon,
      allowOutsideClick: false,
      showCancelButton: true,
      confirmButtonText: 'Si, salir',
      cancelButtonText: 'No.',
    }),
  ).then((result) => {
    if (result.isConfirmed) {
      logout();
    }
  });
};

// validate modal
// `confirmButtonText` es opcional y aditivo: los 20+ usos previos siguen viendo
// «Confirmar». Se añadió porque el diálogo de cerrar sesión de la pantalla de
// elegir panel nombra la acción en su botón, como pide su diseño.
export const sweetalertConfirm = async (
  title,
  text,
  showRedIcon = false,
  confirmButtonText = 'Confirmar',
) => {
  return Swal.fire(
    alertOptions({
      ...withHtmlBody(title, text),
      imageUrl: showRedIcon ? InfoRedIcon : InfoIcon,
      allowOutsideClick: false,
      showCancelButton: true,
      cancelButtonText: 'Cancelar',
      confirmButtonText,
    }),
  ).then((result) => {
    if (result.isConfirmed) {
      return true;
    }
  });
};
